import type { Alignment, DirectedPath } from './contracts.js';
import { ALIGNMENT_HEIGHT, alignmentAt, pointAlong } from './track.js';
import { geoToWorld } from './projection.js';
export interface StationInput { stationId: string; selectionId: string; name: string; position: [number,number] }
export interface StationExtentInput { stationId: string; polygon: [number,number][][] }
export interface TycoonStation extends StationInput { pathId: string; distanceMeters: number; alignment: Alignment; platformTiles: number; extentLengthMeters?: number }
export interface StationModule { stationId: string; alignment: Alignment; pass: 'background'|'foreground'; index: number; position: [number,number]; heading: number; height: number; sortHeight: number; stableId: string; pathId: string; distanceMeters: number }
const project = (point: [number,number], path: DirectedPath): { distance: number; error: number } => {
  const p = geoToWorld(point); let best = { distance:0,error:Infinity };
  for (let i=1;i<path.coordinates.length;i+=1) {
    const a=geoToWorld(path.coordinates[i-1]!),b=geoToWorld(path.coordinates[i]!),dx=b[0]-a[0],dy=b[1]-a[1],l2=dx*dx+dy*dy;
    const t=l2===0?0:Math.max(0,Math.min(1,((p[0]-a[0])*dx+(p[1]-a[1])*dy)/l2)), error=Math.hypot(p[0]-a[0]-dx*t,p[1]-a[1]-dy*t);
    if(error<best.error) best={distance:path.sMeters[i-1]!+(path.sMeters[i]!-path.sMeters[i-1]!)*t,error};
  }
  return best;
};
export const stationTrackError = (point: [number,number], path: DirectedPath): number => project(point,path).error;
/** Marker-to-alignment diagnostic threshold; route ribbons can be offset at interchanges. */
export const STATION_PROJECTION_TOLERANCE_METERS = 20;
export function buildStations(stations: readonly StationInput[], extents: readonly StationExtentInput[], paths: readonly DirectedPath[]): TycoonStation[] {
  if (paths.length === 0) return [];
  const byId = new Map(extents.map((extent) => [extent.stationId,extent]));
  return stations.flatMap((station) => {
    const nearest = paths.map((path) => ({ path, ...project(station.position,path) })).sort((a,b)=>a.error-b.error||a.path.id.localeCompare(b.path.id))[0]!;
    const extent = byId.get(station.selectionId); let platformTiles = 10, extentLengthMeters: number | undefined;
    if (extent) {
      const distances = extent.polygon.flat().map((point) => project(point,nearest.path).distance);
      extentLengthMeters = Math.max(...distances)-Math.min(...distances);
      platformTiles = Math.max(1,Math.ceil(extentLengthMeters/20));
    }
    return [{ ...station,pathId:nearest.path.id,distanceMeters:nearest.distance,alignment:alignmentAt(nearest.path.runs,nearest.distance),platformTiles,extentLengthMeters }];
  });
}

export interface StationPipelineStats {
  loaded: number; extentJoins: number; projectedWithinTolerance: number;
  drawableProducing: number; cullingSurvivors: number;
}
export function stationPipelineStats(
  stations: readonly StationInput[], extents: readonly StationExtentInput[], paths: readonly DirectedPath[],
  survivesCulling: (module: StationModule) => boolean = () => true,
): StationPipelineStats {
  const extentIds = new Set(extents.map((extent) => extent.stationId));
  const built = buildStations(stations, extents, paths);
  const modules = built.map((station) => {
    const path = paths.find((candidate) => candidate.id === station.pathId);
    return path ? stationModules(station, path) : [];
  });
  return {
    loaded: stations.length,
    extentJoins: stations.filter((station) => extentIds.has(station.selectionId)).length,
    projectedWithinTolerance: stations.filter((station) =>
      paths.some((path) => project(station.position, path).error <= STATION_PROJECTION_TOLERANCE_METERS)
    ).length,
    drawableProducing: modules.filter((items) => items.length > 0).length,
    cullingSurvivors: modules.filter((items) => items.some(survivesCulling)).length,
  };
}
export function joinStationArrivals<T extends StationInput,A>(stations: readonly T[], arrivals: Readonly<Record<string,readonly A[]>>): Array<T & { arrivals: readonly A[] }> {
  return stations.map((station) => ({ ...station,arrivals:arrivals[station.selectionId] ?? [] }));
}
export function stationModules(station: TycoonStation, path: DirectedPath): StationModule[] {
  const half=(station.platformTiles-1)/2;
  return Array.from({length:station.platformTiles},(_,index)=>{
    const distanceMeters=station.distanceMeters+(index-half)*20, sample=pointAlong(path,distanceMeters), base=ALIGNMENT_HEIGHT[station.alignment], id=`station:${station.stationId}:${index}`;
    return [
      { stationId:station.stationId,alignment:station.alignment,pass:'background' as const,index,position:sample.geo,heading:sample.heading,height:base,sortHeight:base-0.1,stableId:`${id}:background`,pathId:path.id,distanceMeters },
      { stationId:station.stationId,alignment:station.alignment,pass:'foreground' as const,index,position:sample.geo,heading:sample.heading,height:base,sortHeight:base+0.1,stableId:`${id}:foreground`,pathId:path.id,distanceMeters },
    ];
  }).flat();
}
